<template>
  <div class="chats-list-page">
    <ChatsSubmenu />

    <PageContent>
      <section class="chat-panel rounded-lg border border-gray-200 bg-white">
        <div class="flex items-center gap-3 border-b border-gray-200 p-4">
          <b-input
            v-model.trim="searchQuery"
            icon="fas fa-search"
            placeholder="Rechercher une conversation..."
            expanded
          />
          <b-button
            type="is-light"
            icon-left="sync-alt"
            @click="fetchChats()"
            :loading="refreshing"
          >
            Actualiser
          </b-button>
        </div>

        <div v-if="loading" class="p-6 text-center text-gray-500">
          Chargement des conversations...
        </div>

        <div v-else-if="error" class="p-6">
          <b-message type="is-danger" has-icon>
            {{ error }}
          </b-message>
        </div>

        <div v-else-if="filteredChats.length === 0" class="p-8 text-center text-gray-500">
          Aucune conversation trouvée.
        </div>

        <ul v-else class="divide-y divide-gray-100">
          <li
            v-for="chat in filteredChats"
            :key="chat.uuid"
            class="transition-colors hover:bg-gray-50"
          >
            <button
              type="button"
              class="w-full p-4 text-left"
              @click="openChat(chat)"
            >
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0">
                  <div
                    v-if="isServiceCounterpart(chat)"
                    class="flex h-10 w-10 items-center justify-center rounded-full"
                    :class="serviceBadgeClass(chat)"
                  >
                    <b-icon :icon="serviceIcon(chat)" size="is-small" />
                  </div>
                  <Avatar
                    v-else
                    :user="avatarUser(chat)"
                    size="10"
                  />
                </div>

                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-gray-900">
                        {{ counterpartLabel(chat) }}
                      </p>
                      <p class="truncate text-xs text-gray-500">
                        {{ counterpartSubtitle(chat) }}
                      </p>
                    </div>
                    <div class="flex flex-col items-end">
                      <p class="text-xs text-gray-500">
                        {{ formatDate(lastMessageDate(chat), "dd/MM/yyyy HH:mm") }}
                      </p>
                      <b-tag
                        v-if="unreadCount(chat) > 0"
                        type="is-danger"
                        size="is-small"
                        rounded
                      >
                        {{ unreadCount(chat) }} non lu<span v-if="unreadCount(chat) > 1">s</span>
                      </b-tag>
                    </div>
                  </div>

                  <p class="mt-1 truncate text-sm text-gray-700">
                    {{ lastMessagePreview(chat) }}
                  </p>
                </div>
              </div>
            </button>
          </li>
        </ul>
      </section>
    </PageContent>
  </div>
</template>

<script>
import { parseISO, format } from 'date-fns'
import fr from 'date-fns/locale/fr'
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import Avatar from '@/components/Avatar.vue'
import ChatsSubmenu from './ChatsSubmenu.vue'
import { resolveContactDenomination } from '@/utils/contactDenomination'

export default {
  name: 'ChatsListPage',
  components: {
    PageContent,
    Avatar,
    ChatsSubmenu
  },
  data () {
    return {
      chats: [],
      loading: true,
      refreshing: false,
      error: null,
      searchQuery: '',
      refreshInterval: null
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'application',
      'domain'
    ]),
    cseLabel () {
      return resolveContactDenomination(this.domain, this.application)
    },
    applicationDisplayLabel () {
      return String(this.application?.slug || '').toLowerCase().trim() === 'tract'
        ? 'Tract'
        : 'Maviepro'
    },
    filteredChats () {
      const query = (this.searchQuery || '').toLowerCase()
      if (!query) {
        return this.chats
      }

      return this.chats.filter((chat) => {
        const counterpart = this.counterpart(chat)
        const blob = [
          this.counterpartLabel(chat),
          this.counterpartSubtitle(chat),
          counterpart && counterpart.email ? counterpart.email : '',
          this.lastMessagePreview(chat)
        ].join(' ').toLowerCase()

        return blob.includes(query)
      })
    }
  },
  mounted () {
    this.fetchChats({ showLoader: true })
    this.refreshInterval = window.setInterval(() => {
      this.fetchChats()
    }, 20000)
  },
  beforeDestroy () {
    if (this.refreshInterval) {
      window.clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  },
  methods: {
    fetchChats ({ showLoader = false } = {}) {
      if (showLoader) {
        this.loading = true
      } else {
        this.refreshing = true
      }

      return http.get('/chats/mines').then(({ data }) => {
        this.chats = Array.isArray(data) ? data : []
        this.$store.commit('SET_MY_CHATS', this.chats)
        this.error = null
      }).catch((err) => {
        this.error = (err && err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'Impossible de récupérer les conversations.'
      }).finally(() => {
        this.loading = false
        this.refreshing = false
      })
    },
    openChat (chat) {
      this.$router.push({ name: 'app-chats-chat', params: { uuid: chat.uuid } })
    },
    counterpart (chat) {
      if (chat && chat.counterpart) {
        return chat.counterpart
      }

      if (!chat || !chat.assignee || !chat.initiator || !this.user) {
        return null
      }

      return chat.assignee.uuid === this.user.uuid ? chat.initiator : chat.assignee
    },
    avatarUser (chat) {
      const counterpart = this.counterpart(chat)
      if (counterpart) {
        return counterpart
      }

      return {
        firstname: '?',
        lastname: '?',
        avatar: null
      }
    },
    counterpartLabel (chat) {
      const counterpart = this.counterpart(chat)
      if (!counterpart) {
        return 'Interlocuteur'
      }

      if (counterpart.is_service && counterpart.type === 'cse') {
        return this.cseLabel
      }

      if (counterpart.is_service && counterpart.type === 'maviepro') {
        return `Service technique ${this.applicationDisplayLabel}`
      }

      if (counterpart.label) {
        return counterpart.label
      }

      const fullName = `${counterpart.firstname || ''} ${counterpart.lastname || ''}`.trim()
      if (fullName) {
        return fullName
      }

      return counterpart.email || 'Interlocuteur'
    },
    counterpartSubtitle (chat) {
      const counterpart = this.counterpart(chat)
      if (!counterpart) {
        return ''
      }

      if (counterpart.organization) {
        return counterpart.organization
      }

      if (counterpart.is_service) {
        if (counterpart.type === 'cse') {
          return counterpart.email || `Boîte ${this.cseLabel}`
        }
        if (counterpart.type === 'maviepro') {
          return `Support ${this.applicationDisplayLabel}`
        }
      }

      return counterpart.email || ''
    },
    isServiceCounterpart (chat) {
      const counterpart = this.counterpart(chat)
      return !!(counterpart && counterpart.is_service)
    },
    serviceIcon (chat) {
      const counterpart = this.counterpart(chat)
      if (counterpart && counterpart.type === 'maviepro') {
        return 'fas fa-headset'
      }
      return 'fas fa-envelope'
    },
    serviceBadgeClass (chat) {
      const counterpart = this.counterpart(chat)
      return counterpart && counterpart.type === 'maviepro'
        ? 'bg-teal-600 text-white'
        : 'bg-primary-600 text-white'
    },
    unreadCount (chat) {
      if (chat && typeof chat.newMessagesCounter === 'number') {
        return chat.newMessagesCounter
      }
      if (chat && Array.isArray(chat.newMessages)) {
        return chat.newMessages.length
      }
      return 0
    },
    lastMessage (chat) {
      if (!chat || !Array.isArray(chat.messages) || chat.messages.length === 0) {
        return null
      }
      return chat.messages[chat.messages.length - 1]
    },
    lastMessageDate (chat) {
      const message = this.lastMessage(chat)
      return message ? message.created_at : null
    },
    lastMessagePreview (chat) {
      const message = this.lastMessage(chat)
      if (!message) {
        return 'Aucun message'
      }

      if (message.is_system) {
        return '[Message automatique]'
      }

      if (message.content) {
        return message.content
      }

      if (message.attachment) {
        return '[Pièce jointe]'
      }

      return 'Message'
    },
    formatDate (date, pattern) {
      if (!date) {
        return ''
      }

      const normalized = String(date)
        .trim()
        .replace(/\s+/g, 'T')
        .replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})?$)/, '.$1')

      const parsed = parseISO(normalized)
      if (Number.isNaN(parsed.getTime())) {
        return ''
      }

      return format(parsed, pattern, { locale: fr })
    }
  }
}
</script>

<style scoped lang="scss">
.chat-panel {
  max-width: 1080px;
  margin: 0 auto;
}
</style>
